import type { IPendingCallback } from '@/app/endpoints/commonEndpoints'

const parseLocalDateTime = (naive: string): Date | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(naive)
    if (!match) return null
    const [, year, month, day, hour, minute] = match
    return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute))
}

const pad2 = (n: number) => String(n).padStart(2, '0')

const formatHour = (date: Date) => `${pad2(date.getHours())} h ${pad2(date.getMinutes())}`

/** "Jeudi 24 septembre 2026" */
export function formatCallbackDate(dueDate: string): string {
    const date = parseLocalDateTime(dueDate)
    if (!date) return ''
    const label = date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    return label.charAt(0).toUpperCase() + label.slice(1)
}

/** "13 h 00 – 13 h 30" */
export function formatCallbackSlot(dueDate: string, endDate: string | null): string {
    const start = parseLocalDateTime(dueDate)
    if (!start) return ''
    const end = endDate ? parseLocalDateTime(endDate) : null
    return end ? `${formatHour(start)} – ${formatHour(end)}` : formatHour(start)
}

/** "+33612345678" or "0612345678" → "06 12 34 56 78"; anything else is left as typed */
export function formatFrenchPhone(phone: string | null): string {
    if (!phone) return ''
    const digits = phone.replace(/\D/g, '')
    const national = digits.length === 11 && digits.startsWith('33') ? `0${digits.slice(2)}` : digits
    return national.length === 10 ? national.replace(/(\d{2})(?=\d)/g, '$1 ') : phone
}

/** The slot as the callback form fields expect it, to prefill a reschedule */
export function callbackFormValues(pending: IPendingCallback): { appointmentDate: string; appointmentTime: string } {
    const date = parseLocalDateTime(pending.dueDate)
    if (!date) return { appointmentDate: '', appointmentTime: '' }
    return {
        appointmentDate: `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`,
        appointmentTime: String(date.getHours()),
    }
}

/** "jeu. 24/09 · 13 h 00", the slot being replaced */
export function formatCallbackCurrent(dueDate: string): string {
    const date = parseLocalDateTime(dueDate)
    if (!date) return ''
    const weekday = date.toLocaleDateString('fr-FR', { weekday: 'short' })
    return `${weekday} ${pad2(date.getDate())}/${pad2(date.getMonth() + 1)} · ${formatHour(date)}`
}

const toNaiveDateTime = (date: Date) =>
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}:00`

/** The hourly slot a fresh booking created, when the back cannot be asked for it */
export function pendingCallbackFromForm(appointmentDate: string, appointmentTime: string, phone: string | null): IPendingCallback | null {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(appointmentDate)
    const hour = Number(appointmentTime)
    if (!match || !Number.isInteger(hour)) return null
    const start = new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]), hour)
    const end = new Date(start)
    end.setHours(end.getHours() + 1)
    return { taskId: null, dueDate: toNaiveDateTime(start), endDate: toNaiveDateTime(end), phone, displayedNumber: null }
}

export function pendingCallbackNotice(pending: IPendingCallback | null): string {
    if (!pending) return 'Un rendez-vous est déjà pris pour ce dossier.'
    const date = formatCallbackDate(pending.dueDate)
    return `Un rendez-vous est déjà pris pour ce dossier : ${date.charAt(0).toLowerCase()}${date.slice(1)}, ${formatCallbackSlot(pending.dueDate, pending.endDate)}.`
}
